// src/components/lekhi/LekhiDiagnosticPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getDiagnosticQuestions,
  submitDiagnostic
} from '../../services/firebase/firestoreService';
import './LekhiDiagnosticPage.css';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];

const SUBJECT_LABELS = {
  grammar: { mr: 'मराठी', en: 'Marathi' },
  math: { mr: 'गणित', en: 'Math' },
  reasoning: { mr: 'बुद्धिमत्ता', en: 'Reasoning' },
  gk: { mr: 'सामान्य ज्ञान', en: 'General Knowledge' }
};

const TEXT = {
  title: { mr: 'लेखी चाचणी', en: 'Lekhi Trial' },
  introHeading: { mr: 'तुझा स्तर ओळखूया', en: "Let's find your level" },
  introLine1: {
    mr: 'ही एक छोटी चाचणी आहे जी तुझा सध्याचा स्तर ओळखण्यासाठी आहे.',
    en: 'This is a short trial to find your current level.'
  },
  introPoints: {
    mr: ['एकूण ३२ प्रश्न (४ विषय × ८ प्रश्न)', 'अंदाजे 30 मिनिटे', 'स्वतः उत्तरे दे म्हणजे योग्य स्तर मिळेल'],
    en: ['Total 32 questions (4 subjects × 8)', 'About 30 minutes', 'Answer on your own so we place you right']
  },
  startBtn: { mr: 'चाचणी सुरू कर', en: 'Start Trial' },
  prevBtn: { mr: 'मागील विषय', en: 'Previous Subject' },
  nextBtn: { mr: 'पुढील विषय', en: 'Next Subject' },
  submitBtn: { mr: 'चाचणी जमा कर', en: 'Submit Trial' },
  nudge: { mr: 'सर्व प्रश्न सोडवा', en: 'Please answer all questions' },
  quitConfirm: {
    mr: 'बाहेर पडल्यास तुझी सगळी उत्तरं जातील. पुन्हा सुरू करताना नवीन प्रश्न येतील.',
    en: 'If you leave, all your answers will be lost. New questions will come next time.'
  },
  quitYes: { mr: 'बाहेर पडा', en: 'Leave' },
  quitNo: { mr: 'राहा', en: 'Stay' },
  loading: { mr: 'प्रश्न तयार होत आहेत...', en: 'Loading questions...' },
  loadError: { mr: 'प्रश्न आणता आले नाहीत. पुन्हा प्रयत्न कर.', en: 'Could not load questions. Try again.' },
  submitting: { mr: 'जमा होत आहे...', en: 'Submitting...' },
  resultsMessage: {
    mr: 'तुझा लेखीचा प्रवास इथून सुरू झाला. ऑल द बेस्ट!',
    en: 'Your Lekhi journey starts here. All the best!'
  },
  totalScore: { mr: 'एकूण गुण', en: 'Total Score' },
  thSubject: { mr: 'विषय', en: 'Subject' },
  thScore: { mr: 'गुण', en: 'Score' },
  thLevel: { mr: 'स्तर', en: 'Level' },
  continueBtn: { mr: 'पुढे जा', en: 'Continue' }
};

const ThumbsRain = () => {
 const thumbs = useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 2
    }));
  }, []);
  return (
    <div className="lekhi-diag-thumbs-rain">
      {thumbs.map(t => (
        <span key={t.id} className="lekhi-diag-thumb"
          style={{ left: `${t.left}%`, animationDelay: `${t.delay}s`, animationDuration: `${t.duration}s` }}>
          👍
        </span>
      ))}
    </div>
  );
};

export default function LekhiDiagnosticPage({ onBack }) {
  const { currentUser, refreshUserData } = useAuth();
  const language = localStorage.getItem('spocademy_language') || 'mr';
  const t = (key) => TEXT[key][language] || TEXT[key].mr;

  const [stage, setStage] = useState('intro'); // 'intro' | 'test' | 'results'
  const [questions, setQuestions] = useState(null); // { grammar: [...], math: [...], ... }
  const [loadError, setLoadError] = useState(false);
  const [subjectIndex, setSubjectIndex] = useState(0);
  const [answers, setAnswers] = useState({ grammar: {}, math: {}, reasoning: {}, gk: {} });
  const [showNudge, setShowNudge] = useState(false);
  const [showQuit, setShowQuit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // Preload all 32 questions when intro screen opens
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getDiagnosticQuestions();
        if (!cancelled) {
          setQuestions(data);
          setLoadError(false);
        }
      } catch (e) {
        if (!cancelled) setLoadError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const currentSubject = SUBJECTS[subjectIndex];
  const currentQuestions = questions?.[currentSubject] || [];
  const currentAnswers = answers[currentSubject] || {};
  const allAnsweredInSubject = currentQuestions.length > 0 &&
    currentQuestions.every(q => currentAnswers[q.id]);

  const handleSelectOption = (qId, optionLetter) => {
    setAnswers(prev => ({
      ...prev,
      [currentSubject]: { ...prev[currentSubject], [qId]: optionLetter }
    }));
    if (showNudge) setShowNudge(false);
  };

  const handleNext = () => {
    if (!allAnsweredInSubject) { setShowNudge(true); return; }
    setShowNudge(false);
    setSubjectIndex(i => i + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    setShowNudge(false);
    setSubjectIndex(i => i - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!allAnsweredInSubject) { setShowNudge(true); return; }
    if (!currentUser?.uid || !questions) return;
    setSubmitting(true);
    try {
      const res = await submitDiagnostic(currentUser.uid, questions, answers);
      setResults(res);
      setStage('results');
    } catch (e) {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    await refreshUserData();
    onBack();
  };

  const handleQuit = () => {
    onBack();
  };

  // ===== Intro screen =====
  if (stage === 'intro') {
    return (
      <div className="lekhi-diag-container">
        <div className="lekhi-diag-header">
          <button className="lekhi-diag-back-btn" onClick={onBack}>←</button>
          <div className="lekhi-diag-header-title">{t('title')}</div>
        </div>
        <div className="lekhi-diag-content">
          <div className="lekhi-diag-intro">
            <h2>{t('introHeading')}</h2>
            <p>{t('introLine1')}</p>
            <ul>
              {t('introPoints').map((p, i) => <li key={i}>{p}</li>)}
            </ul>
            {loadError ? (
              <div className="lekhi-diag-status" style={{ color: '#dc3545' }}>{t('loadError')}</div>
            ) : !questions ? (
              <div className="lekhi-diag-status">{t('loading')}</div>
            ) : (
              <button className="lekhi-diag-btn lekhi-diag-btn-next" style={{ width: '100%' }}
                onClick={() => setStage('test')}>
                {t('startBtn')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== Results screen =====
  if (stage === 'results' && results) {
    return (
      <div className="lekhi-diag-container">
        <div className="lekhi-diag-overlay">
          <div className="lekhi-diag-results-box">
            <ThumbsRain />
            <div style={{ position: 'relative', zIndex: 2 }}>
              <p className="lekhi-diag-results-message">{t('resultsMessage')}</p>
              <div className="lekhi-diag-total-score">
                {t('totalScore')}: <strong>{results.totalScore} / {results.totalQuestions}</strong>
              </div>
              <table className="lekhi-diag-results-table">
                <thead>
                  <tr>
                    <th>{t('thSubject')}</th>
                    <th>{t('thScore')}</th>
                    <th>{t('thLevel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {SUBJECTS.map(s => {
                    const r = results.perSubject[s];
                    return (
                      <tr key={s}>
                        <td>{SUBJECT_LABELS[s][language]}</td>
                        <td>{r.score} / {r.total}</td>
                        <td className="level-cell">{r.label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button className="lekhi-diag-results-continue" onClick={handleContinue}>
                {t('continueBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Test screen =====
  const isLastSubject = subjectIndex === SUBJECTS.length - 1;
  const isFirstSubject = subjectIndex === 0;

  return (
    <div className="lekhi-diag-container">
      <div className="lekhi-diag-header">
        <button className="lekhi-diag-back-btn" onClick={() => setShowQuit(true)}>←</button>
        <div className="lekhi-diag-header-title">{t('title')}</div>
        <div className="lekhi-diag-progress-bar">
          {SUBJECTS.map((s, i) => (
            <div key={s}
              className={`lekhi-diag-progress-segment ${
                i < subjectIndex ? 'filled' : i === subjectIndex ? 'active' : ''
              }`} />
          ))}
        </div>
      </div>

      <div className="lekhi-diag-content">
        <div className="lekhi-diag-subject-heading">
          {SUBJECT_LABELS[currentSubject][language]}
        </div>

        {currentQuestions.map((q, idx) => (
          <div key={q.id} className="lekhi-diag-question-card">
            <div className="lekhi-diag-question-number">{idx + 1} / {currentQuestions.length}</div>
            <div className="lekhi-diag-question-text">{q.question}</div>
            {['A', 'B', 'C', 'D'].map(letter => (
              <div key={letter}
                className={`lekhi-diag-option ${currentAnswers[q.id] === letter ? 'selected' : ''}`}
                onClick={() => handleSelectOption(q.id, letter)}>
                <span className="lekhi-diag-option-letter">{letter}.</span>
                <span className="lekhi-diag-option-text">{q.options[letter]}</span>
              </div>
            ))}
          </div>
        ))}

        {showNudge && <div className="lekhi-diag-nudge">{t('nudge')}</div>}

        <div className="lekhi-diag-nav-row">
          {!isFirstSubject && (
            <button className="lekhi-diag-btn lekhi-diag-btn-prev"
              onClick={handlePrev} disabled={submitting}>
              {t('prevBtn')}
            </button>
          )}
          {!isLastSubject && (
            <button className="lekhi-diag-btn lekhi-diag-btn-next"
              onClick={handleNext} disabled={submitting}>
              {t('nextBtn')}
            </button>
          )}
          {isLastSubject && (
            <button className="lekhi-diag-btn lekhi-diag-btn-submit"
              onClick={handleSubmit} disabled={submitting}>
              {submitting ? t('submitting') : t('submitBtn')}
            </button>
          )}
        </div>
      </div>

      {showQuit && (
        <div className="lekhi-diag-overlay">
          <div className="lekhi-diag-confirm-box">
            <p className="lekhi-diag-confirm-text">{t('quitConfirm')}</p>
            <div className="lekhi-diag-confirm-buttons">
              <button className="lekhi-diag-btn lekhi-diag-btn-prev" onClick={() => setShowQuit(false)}>
                {t('quitNo')}
              </button>
              <button className="lekhi-diag-btn lekhi-diag-btn-submit" onClick={handleQuit}>
                {t('quitYes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}